<script>
/**
 * 회원 등록 다이얼로그.
 *
 * user 테이블의 기본 정보(이름, 연락처, 추천지점코드)만 입력하여 등록한다.
 * 사이트/소셜 로그인 정보는 앱 가입 시 생성되므로 관리자 직접 등록에서는 포함하지 않는다.
 *
 * Props:
 *   modelValue (Boolean) - 다이얼로그 표시 여부 (v-model)
 *
 * Emits:
 *   update:modelValue - 다이얼로그 닫기
 *   created           - 등록 성공 시 (목록 새로고침 트리거)
 */
import { createMember } from '@/api/member.js'

export default {
  name: 'MemberCreateDialog',

  props: {
    /** v-model 바인딩: 다이얼로그 표시 여부 */
    modelValue: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'created'],

  data() {
    return {
      /** 입력 폼 데이터 */
      form: {
        name: '',
        phone: '',
        branchCode: '',
      },

      /** Vuetify v-form 유효성 검사 결과 */
      formValid: false,

      /** API 호출 중 여부 */
      loading: false,

      /** 에러 메시지 */
      errorMsg: '',
    }
  },

  computed: {
    /** v-model 연동을 위한 computed getter/setter */
    dialogVisible: {
      get() { return this.modelValue },
      set(val) { this.$emit('update:modelValue', val) },
    },
  },

  watch: {
    /** 다이얼로그가 열릴 때마다 폼 초기화 */
    modelValue(val) {
      if (val) this.resetForm()
    },
  },

  methods: {
    /** 폼 초기화 */
    resetForm() {
      this.form = { name: '', phone: '', branchCode: '' }
      this.errorMsg = ''
      this.loading = false
    },

    /** 다이얼로그 닫기 */
    closeDialog() {
      this.dialogVisible = false
    },

    /**
     * 회원 등록 실행.
     * 유효성 검사 통과 후 API 호출, 성공 시 created 이벤트 emit.
     */
    async handleSubmit() {
      // Vuetify v-form 유효성 검사
      const { valid } = await this.$refs.form.validate()
      if (!valid) return

      this.loading = true
      this.errorMsg = ''

      try {
        const res = await createMember({
          name:       this.form.name,
          phone:      this.form.phone || undefined,
          branchCode: this.form.branchCode || undefined,
        })

        if (res.data?.code === 0) {
          this.$emit('created')
        } else {
          this.errorMsg = res.data?.message || '등록에 실패했습니다.'
        }
      } catch (e) {
        console.error('[MemberCreateDialog] createMember error:', e)
        this.errorMsg = '서버 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    },

    /* ── 유효성 검사 규칙 ── */

    /** 이름 필수 검증 */
    nameRules() {
      return [
        v => !!v?.trim() || '이름은 필수입니다.',
        v => v.length <= 100 || '이름은 100자 이내로 입력해 주세요.',
      ]
    },

    /** 연락처 형식 검증 (선택 입력) */
    phoneRules() {
      return [
        v => !v || /^[0-9\-]{0,20}$/.test(v) || '연락처 형식이 올바르지 않습니다.',
      ]
    },

    /** 지점코드 형식 검증 (선택 입력) */
    branchCodeRules() {
      return [
        v => !v || v.length <= 21 || '지점코드는 21자 이내로 입력해 주세요.',
      ]
    },
  },
}
</script>

<template>
  <v-dialog v-model="dialogVisible" max-width="480" persistent>
    <v-card rounded="lg">
      <!-- 헤더 -->
      <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3 d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-account-plus-outline</v-icon>
        회원 등록
      </v-card-title>
      <v-divider />

      <!-- 폼 -->
      <v-card-text class="pa-5">
        <v-form ref="form" v-model="formValid" @submit.prevent="handleSubmit">
          <!-- 이름 (필수) -->
          <v-text-field
            v-model="form.name"
            label="이름"
            placeholder="회원 이름을 입력하세요"
            variant="outlined"
            density="comfortable"
            :rules="nameRules()"
            class="mb-3"
            autofocus
          />

          <!-- 연락처 (선택) -->
          <v-text-field
            v-model="form.phone"
            label="연락처 (선택)"
            placeholder="01012345678"
            variant="outlined"
            density="comfortable"
            :rules="phoneRules()"
            class="mb-3"
            prepend-inner-icon="mdi-phone-outline"
          />

          <!-- 추천지점코드 (선택) -->
          <v-text-field
            v-model="form.branchCode"
            label="추천지점코드 (선택)"
            placeholder="BRNC2026..."
            variant="outlined"
            density="comfortable"
            :rules="branchCodeRules()"
            hint="지점코드는 21자리입니다"
            persistent-hint
            prepend-inner-icon="mdi-store-outline"
          />
        </v-form>

        <!-- 에러 메시지 -->
        <v-alert
          v-if="errorMsg"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ errorMsg }}
        </v-alert>
      </v-card-text>

      <v-divider />

      <!-- 액션 버튼 -->
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="outlined" :disabled="loading" @click="closeDialog">취소</v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          prepend-icon="mdi-check"
          @click="handleSubmit"
        >
          등록
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
